import {
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CheckOutlined, DeleteOutline } from "@mui/icons-material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addCounty,
  deleteCounty,
  getCounties,
  updateCounty,
} from "../services/settingsService";
import { setCounties } from "../features/settingsSlice";

const CountiesSettings = () => {
  const counties = useSelector((state) => state.settings.value.counties);
  const dispatch = useDispatch();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [assessorUrl, setAssessorUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [urlDrafts, setUrlDrafts] = useState({});

  const refresh = async () => {
    const res = await getCounties();
    if (res) {
      dispatch(setCounties(res));
    }
  };

  const onAdd = async () => {
    setSaving(true);
    try {
      const created = await addCounty({ code, name, assessorUrl });
      if (created) {
        setCode("");
        setName("");
        setAssessorUrl("");
        await refresh();
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (county) => {
    const ok = await deleteCounty(county.id);
    if (ok) {
      await refresh();
    }
  };

  const onSaveUrl = async (county) => {
    const updated = await updateCounty(county.id, {
      assessorUrl: urlDrafts[county.id] ?? "",
    });
    if (updated) {
      setUrlDrafts((drafts) => {
        const next = { ...drafts };
        delete next[county.id];
        return next;
      });
      await refresh();
    }
  };

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add County
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Counties appear in the Create CID dialog. The assessor site URL
            adds an "Assessor Site" link to the Quick Assessor editor for CIDs
            in that county.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase())}
              placeholder="dal"
              inputProps={{ maxLength: 10 }}
              sx={{ width: { sm: 140 } }}
            />
            <TextField
              size="small"
              label="County Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dalton"
              fullWidth
            />
            <TextField
              size="small"
              label="Assessor Site URL (optional)"
              value={assessorUrl}
              onChange={(e) => setAssessorUrl(e.target.value)}
              placeholder="https://..."
              fullWidth
            />
            <Button
              variant="contained"
              onClick={onAdd}
              disabled={saving || code.trim().length < 2 || !name.trim()}
            >
              Add
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Counties ({counties.length})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Assessor Site URL</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {counties.map((county) => {
                const draft = urlDrafts[county.id];
                const dirty =
                  draft !== undefined && draft !== (county.assessor_url || "");
                return (
                  <TableRow key={county.id}>
                    <TableCell sx={{ fontFamily: "monospace" }}>{county.code}</TableCell>
                    <TableCell>{county.name}</TableCell>
                    <TableCell sx={{ width: "50%" }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="https://..."
                        value={draft ?? county.assessor_url ?? ""}
                        onChange={(e) =>
                          setUrlDrafts((drafts) => ({
                            ...drafts,
                            [county.id]: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {dirty ? (
                        <Tooltip title="Save assessor URL">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => onSaveUrl(county)}
                          >
                            <CheckOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Delete county (blocked while any CID uses it)">
                        <IconButton size="small" color="error" onClick={() => onDelete(county)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default CountiesSettings;
